import { createSlice } from '@reduxjs/toolkit';
import { FLOW_STATUS, type FlowStatus } from '@utils/constants';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { CompanyProfile } from '@utils/interface/companyProfile';
import { backendCompletedStepToUiIndex, backendStepsCompletedToUiCount, DEMO_LAB_BACKEND_STEPS, DEMO_LAB_UI_STEP_COUNT } from '@utils/demoLabSteps';
import { getStepsCompletedCount } from '@utils/utils';
import type { StepKey } from '@utils/interface';

export type DemoEnvironment = 'internal_reporting' | 'tse_for_isv' | 'monetise' | 'internal_reporting_search_only' | "tse_for_isv_search_only" | "internal_reporting_for_specified_page" | "";

export interface CompanyInfo {
    website_url: string;
    opportunity_number: string;
    demo_environment: DemoEnvironment;
    search_mode?: string;
    output?: string | null | undefined;
    databricks_flow_type?: string | null;
}

export interface CompanySector {
    industry: string[];
    sub_industry: string[];
    sector_positioning: string;
}

export interface Kpi {
    name: string;
    description: string;
    suggested_chart_type: string;
}

export interface Metrics {
    name: string;
    description: string;
    suggested_chart_type: string;
}

export interface KpiMetrics {
    kpi: Kpi[];
    metrics: Metrics[];
}

export interface StarSchemas {
    fact_tables: FactTable[];
    dimension_tables: Dimension[];
    table_schemas?: SchemaTable[];
    schema_variant?: "star_schema" | "3nf" | null;
    schema_tables?: SchemaTable[];
}

export interface SchemaTable {
    name: string;
    type?: string;
    category?: string;
    purpose?: string;
    columns: Column[];
}

export type FactTable = SchemaTable;

export interface Column {
    name: string;
    type: string;
    is_primary_key: string;
    is_foreign_key: string;
    references?: string;
    reference?: string;
}

export interface Dimension {
    name: string;
    type: string;
    category?: string;
    purpose?: string;
    columns: Column[];
}

export interface SpotterQuestions {
    kpi_metric: string;
    natural_language_question: string;
    follow_up_question: string;
}

export interface ReasoningQuestions {
    id: number;
    capability: string;
    question: string;
    explanation?: string;
}

export interface PowerCombos {
    id: number;
    title: string;
    initial_question: string;
    follow_up_question_1: string;
    follow_up_question_2: string;
    description: string;
}

export interface StakeholderQuestions {
    id: number;
    question: string;
    business_context: string;
}

export interface LiveboardResult {
    liveboard_id: string[];
    liveboard_url: string;
    liveboard_name: string;
    response: string;
}

export interface LiveboardPayload {
    status: "success" | "error" | string;
    result: LiveboardResult;
}

export interface CreateLiveboardAPIResponse {
    message: string;
    model_name: string;
    model_id: string;
    liveboard: LiveboardPayload;
}

/** Customer Portal — navigation row (sidebar) */
export interface PortalNavigationItem {
    id: number;
    label: string;
    icon: string;
}

/** Per-tab configuration for the portal builder (all fields + upload state) */
export interface PortalTabFields {
    menu_tab_name: string;
    icon: string;
    hide_from_menu: boolean;
    options: string;
    liveboard: { id: string | null; name: string } | null;
    spotter_name: string;
    data_source?: { id: string | null; name: string } | null;
    /** Presigned PUT URL for template upload (while preparing upload) */
    presignedUploadUrl?: string | null;
    /** Final template file URL after successful S3 upload (no query string) */
    templateFileUrl?: string | null;
}

/** Single store key for Customer Portal wizard: tabs, order, selection, uploads */
export interface CustomerPortalAppConfig {
    navigationItems: PortalNavigationItem[];
    tabs: Record<number, PortalTabFields>;
    selectedNavId: number;
}

export interface CustomerPortalDefaults {
    liveboard?: unknown;
    dashboard?: unknown;
    genie_space?: unknown;
    data_source?: unknown;
    spotter_model?: unknown;
}

interface DashboardState {
    currentStep: number;
    /** Highest UI step index the user has navigated to (via Next or stepper). */
    maxVisitedStep: number;
    /** UI step index where Auto-Pilot was started; used to restore on stop. */
    silentModeOriginStep: number | null;
    totalSteps: number;
    stepsCompletedCount: number;
    isSilentModeActive: boolean;
    flowStatus: FlowStatus;
    sessionId: string;
    companyInfo: CompanyInfo;
    companyOverview: string;
    discoveryNotes: string;
    useCase: string;
    companySector: CompanySector;
    companyProfile: CompanyProfile | null;
    kpiMetrics: KpiMetrics;
    starSchemas: StarSchemas;
    sampleData: any;
    dataDictionary: any;
    spotterQuestions: SpotterQuestions[];
    reasoningQuestions: ReasoningQuestions[];
    powerCombos: PowerCombos[];
    stakeholderQuestions: StakeholderQuestions[];
    demoCribSheet: any,
    demoCribSheetStructured: any,
    isEditing: boolean;
    model_id: number;
    lockRegenerate: boolean;
    flowData: any[];
    thoughtspotModelId: string | null;
    brandKit: any;
    tsConfig: {
        token: string | null;
        username: string | null;
        cluster_url: string | null;
    };
    authStatus: string;
    isPushedToThoughtspot: boolean;
    clusterStatus: string | null;
    countries: string[];
    isDeployedToSnowflake: boolean;
    snowflakeModelId: number;
    snowflakesTables: string[];
    modelName?: string;
    buildCustomPortal: boolean;
    liveboardDetails: CreateLiveboardAPIResponse | null;
    semanticAssets: boolean;
    environment: string;
    processingState: string;
    processingEntity: number | null;
    pipelineId: number | null;
    entities: any[];
    isProcessingEntity: boolean;
    stepStatus: {
        step1: string;
        step2: string;
        step3: string;
        step4: string;
        step5: string;
        step6: string;
        step7: string;
        step8: string;
        step9: string;
        step10: string;
    }
    versalConfig: any | null;
    modifiedStep: string | null;
    isStep8FileUploaded: boolean;
    versalUrl: string | null;
    searchOnSpecificPage: boolean;
    formulaSet: string | null;
    clusterUrl: string | null;
    templateUploadUrl: string[];
    customerPortalDefaults: CustomerPortalDefaults | null;
    /** Customer Portal: full form + per-tab uploads under one key */
    appConfig: CustomerPortalAppConfig | null;
}

const initialState: DashboardState = {
    currentStep: 0,
    maxVisitedStep: 0,
    silentModeOriginStep: null,
    totalSteps: DEMO_LAB_UI_STEP_COUNT,
    stepsCompletedCount: 0,
    isSilentModeActive: false,
    flowStatus: FLOW_STATUS.NEW,
    isEditing: false,
    sessionId: "",
    companyInfo: {
        website_url: "",
        opportunity_number: "",
        demo_environment: "",
        search_mode: "web_search",
        output: null,
        databricks_flow_type: null,
    },
    companyOverview: "",
    discoveryNotes: "",
    useCase: "",
    companySector: {
        industry: [],
        sub_industry: [],
        sector_positioning: "",
    },
    companyProfile: null,
    kpiMetrics: {
        kpi: [{
            name: "",
            description: "",
            suggested_chart_type: "",
        }],
        metrics: [{
            name: "",
            description: "",
            suggested_chart_type: "",
        }],
    },
    starSchemas: {
        fact_tables: [{
            name: "",
            columns: [
                {
                    name: "",
                    type: "",
                    is_primary_key: "",
                    is_foreign_key: "",
                }
            ],
        }],
        dimension_tables: [
            {
                name: "",
                type: "",
                columns: [
                    {
                        name: "",
                        type: "",
                        is_primary_key: "",
                        is_foreign_key: "",
                    }
                ],
            }
        ],
        table_schemas: [],
        schema_variant: null,
        schema_tables: [],
    },
    sampleData: {},
    dataDictionary: {},
    spotterQuestions: [
        {
            kpi_metric: "",
            natural_language_question: "",
            follow_up_question: ""
        }
    ],
    powerCombos: [
        {
            id: 0,
            title: "",
            initial_question: "",
            follow_up_question_1: "",
            follow_up_question_2: "",
            description: ""
        }
    ],
    reasoningQuestions: [
        {
            id: 0,
            capability: "",
            question: ""
        }
    ],
    stakeholderQuestions: [
        {
            id: 0,
            question: "",
            business_context: ""
        }
    ],
    demoCribSheet: "",
    demoCribSheetStructured: null,
    model_id: 0,
    lockRegenerate: false,
    flowData: [],
    thoughtspotModelId: null,
    brandKit: null,
    tsConfig: {
        token: null,
        username: null,
        cluster_url: null,
    },
    authStatus: "INITIALIZING",
    isPushedToThoughtspot: false,
    clusterStatus: null,
    countries: [],
    isDeployedToSnowflake: false,
    snowflakeModelId: 0,
    snowflakesTables: [],
    modelName: "",
    buildCustomPortal: false,
    liveboardDetails: null,
    semanticAssets: false,
    environment: "",
    processingState: "not-started",
    processingEntity: null,
    pipelineId: null,
    entities: [],
    isProcessingEntity: false,
    stepStatus: {
        step1: "not-started",
        step2: "not-started",
        step3: "not-started",
        step4: "not-started",
        step5: "not-started",
        step6: "not-started",
        step7: "not-started",
        step8: "not-started",
        step9: "not-started",
        step10: "not-started",
    },
    versalConfig: null,
    modifiedStep: null,
    isStep8FileUploaded: false,
    versalUrl: null,
    searchOnSpecificPage: false,
    formulaSet: null,
    clusterUrl: null,
    templateUploadUrl: [],
    customerPortalDefaults: null,
    appConfig: null,
};

/** Backend stage after cancelling Auto-Pilot mid-processing (matches API stop_silent_mode). */
const SILENT_STOP_STAGE_FALLBACK: Record<string, string> = {
    "Step 4 Processing": "Step 3 Finished",
    "Step 5 Processing": "Step 4 Finished",
    "Step 7 Processing": "Step 5 Finished",
    "Step 8 Processing": "Step 7 Finished",
    "Step 9 Processing": "Step 8 Finished",
};

const resolveStageAfterSilentStop = (stage?: string): string | undefined => {
    if (!stage) return undefined;
    return SILENT_STOP_STAGE_FALLBACK[stage] ?? stage;
};

/** Snap wizard to backend progress after Auto-Pilot stop; lock stepper to completed steps only. */
const applyWizardSyncFromBackendStage = (state: DashboardState, stage?: string) => {
    const effectiveStage = resolveStageAfterSilentStop(stage);
    if (!effectiveStage) return;

    const completedSteps = getStepsCompletedCount(effectiveStage);
    const uiIndex = backendCompletedStepToUiIndex(completedSteps);

    state.isSilentModeActive = false;
    state.silentModeOriginStep = null;
    state.stepsCompletedCount = completedSteps;
    state.currentStep = uiIndex;
    state.maxVisitedStep = uiIndex;

    DEMO_LAB_BACKEND_STEPS.forEach((backendStep) => {
        const stepKey = `step${backendStep}` as StepKey;
        if (backendStep < completedSteps) {
            state.stepStatus[stepKey] = `Step ${backendStep} Finished`;
            return;
        }
        if (backendStep === completedSteps) {
            state.stepStatus[stepKey] = effectiveStage.includes("Finished")
                ? effectiveStage
                : `Step ${backendStep} Finished`;
            return;
        }
        state.stepStatus[stepKey] = "not-started";
    });

    (["step2", "step6"] as StepKey[]).forEach((stepKey) => {
        state.stepStatus[stepKey] = "not-started";
    });
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setCurrentStep(state, action: PayloadAction<number>) {
            state.currentStep = action.payload;
            state.maxVisitedStep = Math.max(state.maxVisitedStep, action.payload);
        },
        nextStep(state) {
            state.currentStep += 1;
            state.maxVisitedStep = Math.max(state.maxVisitedStep, state.currentStep);
        },
        prevStep(state) {
            state.currentStep -= 1;
        },
        resetStep(state) {
            state.currentStep = 0;
        },
        setIsEditing(state, action: PayloadAction<boolean>) {
            state.isEditing = action.payload;
        },
        setStepsCompletedCount(state, action: PayloadAction<number>) {
            const previousUiCompleted = backendStepsCompletedToUiCount(state.stepsCompletedCount);
            const nextUiCompleted = backendStepsCompletedToUiCount(action.payload);
            state.stepsCompletedCount = action.payload;
            state.flowStatus = action.payload === 0 ? FLOW_STATUS.NEW : FLOW_STATUS.IN_PROGRESS;
            if (nextUiCompleted < previousUiCompleted) {
                state.maxVisitedStep = Math.min(
                    state.maxVisitedStep,
                    Math.max(nextUiCompleted - 1, 0)
                );
            }
            state.maxVisitedStep = Math.max(state.maxVisitedStep, state.currentStep);
        },
        startSilentMode(state) {
            state.silentModeOriginStep = state.currentStep;
            state.isSilentModeActive = true;
        },
        stopSilentMode(state) {
            state.isSilentModeActive = false;
        },
        setSilentModeActive(state, action: PayloadAction<boolean>) {
            if (action.payload) {
                if (!state.isSilentModeActive) {
                    state.silentModeOriginStep = state.currentStep;
                }
                state.isSilentModeActive = true;
                return;
            }
            state.isSilentModeActive = false;
        },
        syncWizardAfterSilentModeStop(state, action: PayloadAction<string | undefined>) {
            applyWizardSyncFromBackendStage(state, action.payload);
        },
        setFlowStatus(state, action: PayloadAction<FlowStatus>) {
            state.flowStatus = action.payload;
        },
        setCompanyInfo(state, action: PayloadAction<CompanyInfo>) {
            state.companyInfo = action.payload;
        },
        setCompanyOverview(state, action: PayloadAction<string>) {
            state.companyOverview = action.payload;
        },
        setDiscoveryNotes(state, action: PayloadAction<string>) {
            state.discoveryNotes = action.payload;
        },
        setUseCase(state, action: PayloadAction<string>) {
            state.useCase = action.payload;
        },
        setcompanySector(state, action: PayloadAction<CompanySector>) {
            state.companySector = action.payload;
        },
        setCompanyProfile(state, action: PayloadAction<CompanyProfile | null>) {
            state.companyProfile = action.payload;
        },
        setKpiMetrics(state, action: PayloadAction<KpiMetrics>) {
            state.kpiMetrics = action.payload;
        },
        setStarSchemas(state, action: PayloadAction<StarSchemas>) {
            state.starSchemas = action.payload;
        },
        setSessionId(state, action: PayloadAction<string>) {
            state.sessionId = action.payload;
        },
        setSampleData(state, action: PayloadAction<any>) {
            state.sampleData = action.payload;
        },
        setDataDictionary(state, action: PayloadAction<any>) {
            state.dataDictionary = action.payload;
        },
        setSpotterQuestions(state, action: PayloadAction<SpotterQuestions[]>) {
            state.spotterQuestions = action.payload;
        },
        setReasoningQuestions(state, action: PayloadAction<ReasoningQuestions[]>) {
            state.reasoningQuestions = action.payload;
        },
        setPowerCombos(state, action: PayloadAction<PowerCombos[]>) {
            state.powerCombos = action.payload;
        },
        setStakeholderQuestions(state, action: PayloadAction<StakeholderQuestions[]>) {
            state.stakeholderQuestions = action.payload;
        },
        setDemoCribSheet(state, action: PayloadAction<any>) {
            state.demoCribSheet = action.payload;
        },
        setDemoCribSheetStructured(state, action: PayloadAction<any>) {
            state.demoCribSheetStructured = action.payload;
        },
        setModelId(state, action: PayloadAction<number>) {
            state.model_id = action.payload;
        },
        setLockRegenerate(state, action: PayloadAction<boolean>) {
            state.lockRegenerate = action.payload;
        },
        setFlowData(state, action: PayloadAction<any[]>) {
            state.flowData = action.payload;
        },
        setThoughtspotModelId(state, action: PayloadAction<string>) {
            state.thoughtspotModelId = action.payload;
        },
        setBrandKit(state, action: PayloadAction<any>) {
            state.brandKit = action.payload;
        },
        setTsConfig(state, action: PayloadAction<any>) {
            state.tsConfig = action.payload;
        },
        setAuthStatus(state, action: PayloadAction<string>) {
            state.authStatus = action.payload;
        },
        setIsPushedToThoughtspot(state, action: PayloadAction<boolean>) {
            state.isPushedToThoughtspot = action.payload;
        },
        setClusterStatus(state, action: PayloadAction<string>) {
            state.clusterStatus = action.payload;
        },
        setCountries(state, action: PayloadAction<string[]>) {
            state.countries = action.payload;
        },
        setIsDeployedToSnowflake(state, action: PayloadAction<boolean>) {
            state.isDeployedToSnowflake = action.payload;
        },
        setSnowflakeModelId(state, action: PayloadAction<number>) {
            state.snowflakeModelId = action.payload;
        },
        setSnowflakesTables(state, action: PayloadAction<string[]>) {
            state.snowflakesTables = action.payload;
        },
        setModelName(state, action: PayloadAction<string>) {
            state.modelName = action.payload;
        },
        setBuildCustomPortal(state, action: PayloadAction<boolean>) {
            state.buildCustomPortal = action.payload;
        },
        setLiveboardDetails(state, action: PayloadAction<CreateLiveboardAPIResponse>) {
            state.liveboardDetails = action.payload;
        },
        setSemanticAssets(state, action: PayloadAction<boolean>) {
            state.semanticAssets = action.payload;
        },
        setEnvironment(state, action: PayloadAction<string>) {
            state.environment = action.payload;
        },
        setProcessingState(state, action: PayloadAction<string>) {
            state.processingState = action.payload;
        },
        setProcessingEntity(state, action: PayloadAction<number | null>) {
            state.processingEntity = action.payload;
        },
        setPipelineId(state, action: PayloadAction<number | null>) {
            state.pipelineId = action.payload;
        },
        setEntities(state, action: PayloadAction<any[]>) {
            state.entities = action.payload;
        },
        setIsProcessingEntity(state, action: PayloadAction<boolean>) {
            state.isProcessingEntity = action.payload;
        },
        setStepStatus(
            state,
            action: PayloadAction<{ step: keyof typeof state.stepStatus; status: string }>
        ) {
            const { step, status } = action.payload;
            state.stepStatus[step] = status;
        },
        setVersalConfig(state, action: PayloadAction<any>) {
            state.versalConfig = action.payload;
        },
        setModifiedStep(state, action: PayloadAction<string | null>) {
            state.modifiedStep = action.payload;
        },
        setStep8FileUploaded(state, action: PayloadAction<boolean>) {
            state.isStep8FileUploaded = action.payload;
        },
        setVersalUrl(state, action: PayloadAction<string | null>) {
            state.versalUrl = action.payload;
        },
        setSearchOnSpecificPage(state, action: PayloadAction<boolean>) {
            state.searchOnSpecificPage = action.payload;
        },
        setFormulaSet(state, action: PayloadAction<string | null>) {
            state.formulaSet = action.payload;
        },
        setClusterUrl(state, action: PayloadAction<string | null>) {
            state.clusterUrl = action.payload;
        },
        setTemplateUploadUrl(
            state,
            action: PayloadAction<{ navId: number; uploadUrl: string | null }>
        ) {
            const { navId, uploadUrl } = action.payload;
            const next = [...state.templateUploadUrl];
            next[navId] = uploadUrl ?? "";
            state.templateUploadUrl = next;
        },
        setCustomerPortalAppConfig(
            state,
            action: PayloadAction<CustomerPortalAppConfig | null>
        ) {
            state.appConfig = action.payload;
        },
        setCustomerPortalDefaults(
            state,
            action: PayloadAction<CustomerPortalDefaults | null>
        ) {
            state.customerPortalDefaults = action.payload;
        },
        resetAll() {
            return initialState;
        },
    },
});

export const {
    setCurrentStep,
    nextStep,
    prevStep,
    resetStep,
    setIsEditing,
    setStepsCompletedCount,
    startSilentMode,
    stopSilentMode,
    setSilentModeActive,
    syncWizardAfterSilentModeStop,
    setFlowStatus,
    setCompanyInfo,
    setCompanyOverview,
    setDiscoveryNotes,
    setUseCase,
    setcompanySector,
    setCompanyProfile,
    setKpiMetrics,
    setStarSchemas,
    setSessionId,
    setSampleData,
    setDataDictionary,
    setSpotterQuestions,
    setReasoningQuestions,
    setPowerCombos,
    setStakeholderQuestions,
    setDemoCribSheet,
    setDemoCribSheetStructured,
    setModelId,
    setLockRegenerate,
    setFlowData,
    setThoughtspotModelId,
    setBrandKit,
    setTsConfig,
    setAuthStatus,
    setIsPushedToThoughtspot,
    setClusterStatus,
    setCountries,
    setIsDeployedToSnowflake,
    setSnowflakeModelId,
    setSnowflakesTables,
    setModelName,
    setBuildCustomPortal,
    setLiveboardDetails,
    setSemanticAssets,
    setEnvironment,
    setProcessingState,
    setProcessingEntity,
    setPipelineId,
    setEntities,
    setIsProcessingEntity,
    setStepStatus,
    setVersalConfig,
    setModifiedStep,
    setStep8FileUploaded,
    setVersalUrl,
    setSearchOnSpecificPage,
    setFormulaSet,
    setClusterUrl,
    setTemplateUploadUrl,
    setCustomerPortalAppConfig,
    setCustomerPortalDefaults,
    resetAll
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
